import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { AppShellSkeleton } from '@/app/AppShell';
import { router } from '@/app/router';
import { useAuthStore } from '@/features/auth/auth.store';
import '@/styles/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // 401 já é tratado pelo cliente HTTP, que renova e repete. Repetir aqui
      // multiplicaria as tentativas sem resolver nada.
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Restaura a sessão antes de renderizar.
 *
 * Só o refresh token sobrevive ao recarregamento; o access vive em memória.
 * Sem esta troca inicial, quem recarrega a página cairia na tela de login
 * mesmo com sessão válida.
 */
function Bootstrap() {
  const [ready, setReady] = useState(false);
  const refreshToken = useAuthStore((state) => state.refreshToken);

  useEffect(() => {
    if (!refreshToken) {
      setReady(true);
      return;
    }
    void useAuthStore
      .getState()
      .refresh()
      .finally(() => setReady(true));
    // Só na montagem: renovar a cada mudança do token faria laço.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Esqueleto da casca em vez de spinner: a troca de token costuma levar
  // poucas centenas de milissegundos, e desenhar o layout final desde já evita
  // que a tela salte quando ele aparece.
  if (!ready) return <AppShellSkeleton />;

  return <RouterProvider router={router} />;
}

const container = document.getElementById('root')!;

/**
 * Um root por container, mesmo se este módulo reexecutar.
 *
 * No desenvolvimento o HMR pode reavaliar o entrypoint; um segundo
 * `createRoot` sobre o mesmo nó cria duas árvores React disputando o mesmo
 * DOM. O sintoma engana: a aplicação parece "não reagir" — o clique atualiza
 * o estado de uma árvore enquanto a outra continua pintando a tela antiga —
 * e aparecem erros de `removeChild` sem relação aparente com a causa.
 */
declare global {
  interface Window {
    __centralRoot?: ReturnType<typeof createRoot>;
  }
}

const root = (window.__centralRoot ??= createRoot(container));

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Bootstrap />
    </QueryClientProvider>
  </StrictMode>,
);
